import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, nextTick, ref, watch } from 'vue';
import type { AxiosError } from 'axios';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useStore } from '@/store';
import { useToast } from '@/utils/bootstrapVueUtils';
import {
    getAppStoreInstallUrl,
    getSubscriptionMonthlyPrice,
    isAppStoreType,
    requiresStoreSubscription,
    sharesSubscriptionByType,
    storeTypeOptions
} from '@/utils/stores';
import { addStore } from '@/api/customily/stores';
import CModal from '@/components/shared/CModal.vue';
import CSelect, { type CSelectItem } from '@/components/shared/CSelect';
import CInput from '@/components/shared/CInput.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import CInfo from '@/components/shared/CInfo.vue';
import CError from '@/components/shared/CError.vue';
import { StoreType, type Store } from '@extTypes/api/customily/user';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddStoreModal',
  props: {
    value: { type: Boolean }
  },
  emits: ["input", "added"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: boolean): void;
    (event: 'added', store: Store): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    value: boolean;
};

const DEFAULT_STORE_TYPE = storeTypeOptions.find((o) => o.value === StoreType.Shopify)!;





const { state, commit } = useStore();
const bvToast = useToast();

const formRef: Ref<InstanceType<typeof ValidationObserver> | null> = ref(null);
const storeTypeOption: Ref<CSelectItem<StoreType>> = ref(DEFAULT_STORE_TYPE);
const storeUrl = ref('');
const saving = ref(false);
const errorMessage = ref('');

const requiresSubscription = computed(() =>
    requiresStoreSubscription(storeTypeOption.value.value, state.user?.customilyUser.Stores || [])
);

const subscriptionMonthlyPrice = computed(() => getSubscriptionMonthlyPrice(storeTypeOption.value.value));
const sharesSubscription = computed(() => sharesSubscriptionByType(storeTypeOption.value.value));

const urlField = computed(() => {
    switch (storeTypeOption.value.value) {
        case StoreType.Shopify: {
            return { label: 'Store URL', placeholder: 'YourStore.myshopify.com', rules: 'required|shopify-url' };
        }
        case StoreType.Shoplazza: {
            return { label: 'Store URL', placeholder: 'YourStore.myshoplaza.com', rules: 'required|shoplazza-url' };
        }
        case StoreType.Etsy: {
            return { label: 'Store URL', placeholder: 'etsy.com/shop/YourStore', rules: 'required|url' };
        }
        case StoreType.Amazon: {
            return { label: 'Store Name', placeholder: 'YourStoreName', rules: 'required|alpha_num' };
        }
        default: {
            return { label: 'Store URL', placeholder: 'YourStore.com', rules: 'required|url' };
        }
    }
});

const isFormInvalid = computed(() => formRef.value?.flags.invalid ?? true);

const submitText = computed(() => {
    if (saving.value) return 'Adding Store...';
    return requiresSubscription.value ? 'Subscribe & Add Store' : 'Add Store';
});

watch(
    () => props.value,
    async (isOpen) => {
        if (!isOpen) return;

        storeTypeOption.value = DEFAULT_STORE_TYPE;
        storeUrl.value = '';
        errorMessage.value = '';

        // The modal content is kept between openings, clear the validation state of the previous one
        await nextTick();
        formRef.value?.reset();
    }
);

const close = () => emit('input', false);

const toast = (message: string, variant: 'success' | 'danger' = 'success') => {
    bvToast?.toast(message, { title: 'Add Store', variant, solid: true });
};

// The app store install adds the store to the user, and bills it
const installFromAppStore = (type: StoreType) => {
    window.open(getAppStoreInstallUrl(type, storeUrl.value), '_blank', 'noopener');
    close();
    toast('Complete the Customily installation in the new tab to add the store.');
};

const addStoreAndSubscribe = async (type: StoreType) => {
    // Opened during the click because popup blockers reject the windows opened after an await
    const checkoutWindow = requiresSubscription.value ? window.open('', '_blank') : null;
    if (checkoutWindow) checkoutWindow.opener = null;

    saving.value = true;
    errorMessage.value = '';

    try {
        const { store, checkoutUrl } = await addStore(storeUrl.value, type);

        // The store is added once the user subscribes, so a cancelled checkout leaves nothing behind
        if (checkoutUrl) {
            if (checkoutWindow) {
                checkoutWindow.location.href = checkoutUrl;
            } else {
                window.open(checkoutUrl, '_blank', 'noopener');
            }
            close();
            toast('Complete the subscription in the new tab, the store will be added once it is confirmed.');
        } else if (store) {
            checkoutWindow?.close();
            commit('addUserStore', store);
            emit('added', store);
            close();
            toast(`${store.Url} was added.`);
        }
    } catch (error_) {
        checkoutWindow?.close();
        const error = error_ as AxiosError<{ Message?: string }>;

        if (error.response?.status === 400 && error.response.data?.Message) {
            errorMessage.value = error.response.data.Message;
        } else {
            errorMessage.value = 'Error adding the store, please try again.';
            window.captureException(error);
        }
    } finally {
        saving.value = false;
    }
};

const onSubmit = () => {
    if (isFormInvalid.value || saving.value) return;

    const type = storeTypeOption.value.value;
    if (isAppStoreType(type)) {
        installFromAppStore(type);
        return;
    }
    addStoreAndSubscribe(type);
};

return { __sfc: true,DEFAULT_STORE_TYPE, props, emit, state, commit, bvToast, formRef, storeTypeOption, storeUrl, saving, errorMessage, requiresSubscription, subscriptionMonthlyPrice, sharesSubscription, urlField, isFormInvalid, submitText, close, toast, installFromAppStore, addStoreAndSubscribe, onSubmit, ValidationObserver, ValidationProvider, storeTypeOptions, CModal, CSelect, CInput, CButton, CInfo, CError }
}

})